import React, {ReactElement, ReactNode, useEffect, useRef, useState} from 'react';
import {Typography} from '@material-ui/core';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import StarIcon from '@material-ui/icons/Star';
import {useTranslation} from 'react-i18next';
import MedopsLogo from '@components/system/MyIcons/MedopsLogo';
import './PublicPageLayout.scss';

export type PublicPageContentLayout = 'card' | 'session' | 'plain';

export type PublicPageLayoutProps = {
  children: ReactNode;
  className?: string;
  testId?: string;
  practiceBadgeLabel?: string;
  practiceBadgeIcon?: ReactNode;
  heroBadge?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  description?: ReactNode;
  topContent?: ReactNode;
  showHero?: boolean;
  contentLayout?: PublicPageContentLayout;
  practiceName?: string;
  practiceSubtitle?: string;
  addressLine1?: string;
  addressLine2?: string;
  practicePhones?: ReactNode;
  showPracticeInfo?: boolean;
  footerInfoText?: string;
  copyrightText?: string;
  showFooter?: boolean;
  afterContent?: ReactNode;
};

const PublicPageLayout = ({
  children,
  className = '',
  testId = 'pcl-publicPageLayout',
  practiceBadgeLabel,
  practiceBadgeIcon,
  heroBadge,
  title,
  subtitle,
  description,
  topContent,
  showHero = true,
  contentLayout = 'card',
  practiceName,
  practiceSubtitle,
  addressLine1,
  addressLine2,
  practicePhones,
  showPracticeInfo = true,
  footerInfoText,
  copyrightText,
  showFooter = true,
  afterContent
}: PublicPageLayoutProps): ReactElement => {
  const {t} = useTranslation();
  const logoRowRef = useRef<HTMLDivElement>(null);
  const [isLogoRowWrapped, setIsLogoRowWrapped] = useState(false);

  useEffect(() => {
    const row = logoRowRef.current;
    if (!row) {
      return undefined;
    }

    const checkWrap = () => {
      window.requestAnimationFrame(() => {
        const rowChildren = Array.from(row.children) as HTMLElement[];
        if (rowChildren.length < 2) {
          setIsLogoRowWrapped(false);
          return;
        }
        setIsLogoRowWrapped(rowChildren[0].offsetTop !== rowChildren[1].offsetTop);
      });
    };

    checkWrap();

    const observer = new ResizeObserver(checkWrap);
    observer.observe(row);
    Array.from(row.children).forEach(rowChild => observer.observe(rowChild));

    window.addEventListener('resize', checkWrap);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkWrap);
    };
  }, [practiceBadgeLabel]);

  const logoRowClassName = [
    'logo-wrapper',
    'public-page-layout__logo-row',
    isLogoRowWrapped ? 'public-page-layout__logo-row--wrapped' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const resolvedFooterInfoText =
    footerInfoText ?? t('GUARANTOR_PORTAL.LOGIN_SCREEN.FOOTER_INFO_TEXT');
  const resolvedCopyrightText =
    copyrightText ?? t('GUARANTOR_PORTAL.LOGIN_SCREEN.COPY_RIGHT_TEXT');

  const hasHeroContent =
    Boolean(topContent) || Boolean(title) || Boolean(subtitle) || Boolean(description);

  const hasPracticeDetails =
    Boolean(practiceName) ||
    Boolean(practiceSubtitle) ||
    Boolean(addressLine1) ||
    Boolean(addressLine2) ||
    Boolean(practicePhones);

  const mainPanelClassName = [
    'public-page-layout__main-panel',
    contentLayout === 'session' ? 'public-page-layout__main-panel--session' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const renderHero = (): ReactNode => {
    if (!showHero || !hasHeroContent) {
      return null;
    }

    if (topContent) {
      return <section className="public-page-layout__hero">{topContent}</section>;
    }

    return (
      <section className="public-page-layout__hero">
        {heroBadge ? <div className="public-page-layout__hero-badge">{heroBadge}</div> : null}
        {title ? <h1 className="public-page-layout__hero-title">{title}</h1> : null}
        {subtitle ? <p className="public-page-layout__hero-subtitle">{subtitle}</p> : null}
        {description ? <p className="public-page-layout__hero-description">{description}</p> : null}
      </section>
    );
  };

  const renderPracticeInfo = (): ReactNode => {
    if (!showPracticeInfo || !hasPracticeDetails) {
      return null;
    }

    return (
      <article className="public-page-layout__practice-info clinic-details">
        <div className="clinic-details-wrapper">
          <div className="clinic-title-wrapper">
            <div className="clinic-icon-wrapper">
              <HomeOutlinedIcon color="primary" />
            </div>
            <div>
              {practiceName ? (
                <Typography component="h2" variant="h1">
                  {practiceName}
                </Typography>
              ) : null}
              {practiceSubtitle ? <p className="clinic-tagline">{practiceSubtitle}</p> : null}
            </div>
          </div>
          <address className="clinic-address">
            {addressLine1 ? <p>{addressLine1}</p> : null}
            {addressLine2 ? <p>{addressLine2}</p> : null}
            {practicePhones ? <div>{practicePhones}</div> : null}
          </address>
        </div>
      </article>
    );
  };

  const renderMainContent = (): ReactNode => {
    if (contentLayout === 'card') {
      return <div className="public-page-layout__card">{children}</div>;
    }

    return children;
  };

  return (
    <div
      className={['public-page-layout', className].filter(Boolean).join(' ')}
      data-test-id={testId}>
      <div className="public-page-layout__banner banner">
        <div className="public-page-layout__banner-bg" aria-hidden="true" />
        <div className="public-page-layout__wave" aria-hidden="true">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0,48 C360,96 720,0 1080,40 C1260,56 1380,64 1440,48 L1440,80 L0,80 Z"
              fill="#ffffff"
            />
          </svg>
        </div>

        <div ref={logoRowRef} className={logoRowClassName}>
          <MedopsLogo color="white" className="logo" />
          {practiceBadgeLabel ? (
            <div className="public-page-layout__practice-badge">
              {practiceBadgeIcon ?? <StarIcon fontSize="small" />}
              <span>{practiceBadgeLabel}</span>
            </div>
          ) : null}
        </div>

        {renderHero()}

        <div className={mainPanelClassName}>
          <div className="public-page-layout__content">
            {renderMainContent()}
            {contentLayout !== 'session' ? renderPracticeInfo() : null}
          </div>
        </div>
      </div>

      {afterContent}

      {showFooter ? (
        <div className="numbers-wrapper public-page-layout__footer">
          <Typography variant="h3">{resolvedFooterInfoText}</Typography>
          <small className="copyright">{resolvedCopyrightText}</small>
        </div>
      ) : null}
    </div>
  );
};

export default PublicPageLayout;
